import { useState } from "react";
import { Star, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import SafeAvatar from "@/components/SafeAvatar";

/**
 * Modal de notation d'un pro par un particulier.
 * @param invite { pro, lead_id, lead_title }
 */
export default function ReviewModal({ open, onOpenChange, invite, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open || !invite) return null;
  const pro = invite.pro;

  const submit = async () => {
    if (rating < 1) { toast.error("Merci de sélectionner une note"); return; }
    setBusy(true);
    try {
      await api.post("/reviews", {
        pro_id: pro.id,
        rating,
        comment: comment.trim() || undefined,
        lead_id: invite.lead_id,
      });
      toast.success("Merci pour votre avis !");
      onSubmitted?.();
      onOpenChange(false);
      setRating(0); setComment("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="review-modal">
      <div className="bg-white w-full sm:max-w-md sm:mx-4 sm:rounded-3xl rounded-t-3xl shadow-2xl">
        <div className="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
          <div className="font-heading font-bold text-ink">Noter ce professionnel</div>
          <button onClick={() => onOpenChange(false)} className="text-slate2 tap-scale" data-testid="review-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <SafeAvatar src={pro?.avatar} role="pro" name={pro?.name} className="w-14 h-14 rounded-2xl object-cover border border-border" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-ink truncate">{pro?.name}</div>
              <div className="text-xs text-slate2 truncate">{pro?.company || pro?.trade?.replace(/-/g, " ")}</div>
              <div className="text-[11px] text-slate-400 mt-0.5 truncate">Chantier : {invite.lead_title}</div>
            </div>
          </div>

          <div className="text-center">
            <div className="text-xs uppercase tracking-widest text-slate2 mb-2">Votre note</div>
            <div className="flex items-center justify-center gap-1.5" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((n) => {
                const active = n <= (hover || rating);
                return (
                  <button
                    key={n}
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)}
                    className="tap-scale"
                    data-testid={`review-star-${n}`}
                  >
                    <Star className={`w-9 h-9 ${active ? "fill-brand text-brand" : "text-slate-300"}`} />
                  </button>
                );
              })}
            </div>
            <div className="text-xs text-slate2 mt-1.5 h-4">
              {rating === 0 && "Sélectionnez une note"}
              {rating === 1 && "Décevant"}
              {rating === 2 && "Insuffisant"}
              {rating === 3 && "Correct"}
              {rating === 4 && "Bien"}
              {rating === 5 && "Excellent"}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Votre commentaire (optionnel)</label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder="Ponctualité, qualité du travail, respect du devis…"
              className="rounded-xl mt-1"
              data-testid="review-comment-input"
              maxLength={1500}
            />
            <div className="text-[10px] text-slate-400 text-right mt-1">{comment.length}/1500</div>
          </div>

          <Button
            onClick={submit}
            disabled={busy || rating < 1}
            className="w-full rounded-full bg-brand hover:bg-brand-hover"
            data-testid="review-submit-btn"
          >
            {busy ? "Envoi…" : "Publier mon avis"}
          </Button>
        </div>
      </div>
    </div>
  );
}
